import React, { useState, useEffect } from 'react';
import { X, Settings, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { toast } from "sonner";
import AutocompleteInput from './AutocompleteInput';

const SettingsModal = ({ isOpen, onClose }) => {
  console.log('SettingsModal render - isOpen:', isOpen);
  
  const { user, preferences, updatePreferences } = useUserPreferences();
  const [currentStep, setCurrentStep] = useState(1);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Form state - initialize with current preferences
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    coordinates: null,
    temperatureBias: 'neutral',
    isNewcomer: false,
    arrivalMonth: '',
    originCity: '',
    defaultActivity: 'walk'
  });

  // Store original state for undo functionality
  const [originalData, setOriginalData] = useState(null);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && preferences) {
      const getTemperatureBiasFromLayerShift = (layerShift) => {
        switch (layerShift) {
          case 1: return 'cold';
          case -1: return 'warm';
          case 0:
          default: return 'neutral';
        }
      };

      const initialData = {
        name: preferences.name || '',
        location: preferences.home_city || '',
        coordinates: null,
        temperatureBias: getTemperatureBiasFromLayerShift(preferences.layer_shift),
        isNewcomer: !!preferences.arrival_month,
        arrivalMonth: preferences.arrival_month || '',
        originCity: preferences.prev_city || '',
        defaultActivity: preferences.default_mode || 'walk'
      };

      setFormData(initialData);
      setOriginalData({ ...initialData }); // Store original for undo
      setHasChanges(false);
      setCurrentStep(1);
    }
  }, [isOpen, preferences]);

  const handleDataChange = async (newData) => {
    const updatedData = { ...formData, ...newData };
    setFormData(updatedData);
    setHasChanges(true);
    
    // Auto-save the changes
    try {
      const getLayerShiftFromTemperatureBias = (bias) => {
        switch (bias) {
          case 'cold': return 1;
          case 'warm': return -1;
          case 'neutral':
          default: return 0;
        }
      };

      const updatedPrefs = {
        name: updatedData.name,
        home_city: updatedData.location,
        layer_shift: getLayerShiftFromTemperatureBias(updatedData.temperatureBias),
        default_mode: updatedData.defaultActivity,
        arrival_month: updatedData.isNewcomer ? updatedData.arrivalMonth : null,
        prev_city: updatedData.isNewcomer ? updatedData.originCity : null,
        updated_at: new Date().toISOString()
      };

      await updatePreferences(updatedPrefs);
      console.log('Auto-saved preferences');
    } catch (error) {
      console.error('Auto-save failed:', error);
      // Don't show error to user for auto-save failures
    }
  };


  const handleUndo = async () => {
    if (originalData) {
      setFormData({ ...originalData });
      setHasChanges(false);
      
      // Auto-save the reverted changes
      try {
        const getLayerShiftFromTemperatureBias = (bias) => {
          switch (bias) {
            case 'cold': return 1;
            case 'warm': return -1;
            case 'neutral':
            default: return 0;
          }
        };

        const revertedPrefs = {
          name: originalData.name,
          home_city: originalData.location,
          layer_shift: getLayerShiftFromTemperatureBias(originalData.temperatureBias),
          default_mode: originalData.defaultActivity,
          arrival_month: originalData.isNewcomer ? originalData.arrivalMonth : null,
          prev_city: originalData.isNewcomer ? originalData.originCity : null,
          updated_at: new Date().toISOString()
        };

        await updatePreferences(revertedPrefs);
        toast.success('Settings reverted successfully!');
        console.log('Undo completed and auto-saved');
      } catch (error) {
        console.error('Undo auto-save failed:', error);
        toast.error('Failed to revert settings. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  const stepTitles = [
    'Personal Information',
    'Temperature Preference', 
    'Climate & Activity'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              Settings
            </h2>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Warning Banner */}
        {hasChanges && (
          <div className="mx-6 mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                This impacts your personalization
              </p>
            </div>
          </div>
        )}

        {/* Progress indicator */}
        <div className="flex justify-center space-x-2 my-4">
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                step <= currentStep
                  ? 'bg-blue-500 dark:bg-blue-400'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
            />
          ))}
        </div>

        {/* Step title */}
        <div className="text-center mb-4 px-6">
          <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
            {stepTitles[currentStep - 1]}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Step {currentStep} of 3
          </p>
        </div>

        {/* Step Content */}
        <div className="px-6 pb-6">
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleDataChange({ name: e.target.value })}
                  className="w-full p-3 rounded-lg border border-gray-300 dark:border-gray-600 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Location
                </label>
                <AutocompleteInput
                  onLocationSelect={(locationData) => {
                    handleDataChange({ 
                      location: locationData.location,
                      coordinates: locationData.coordinates
                    });
                  }}
                  theme="light"
                  placeholder="Search for your city"
                  initialValue={formData.location}
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  How do you typically feel about temperature?
                </label>
                <div className="space-y-3">
                  {[
                    { value: 'cold', label: 'I usually feel cold', icon: '🧊' },
                    { value: 'neutral', label: 'I feel just right', icon: '😊' },
                    { value: 'warm', label: 'I usually feel warm', icon: '🔥' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleDataChange({ temperatureBias: option.value })}
                      className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                        formData.temperatureBias === option.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="text-xl mr-3">{option.icon}</span>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {option.label}
                          </div>
                        </div>
                        {formData.temperatureBias === option.value && (
                          <div className="text-blue-500 ml-2">✓</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Are you new to this climate?
                </label>
                <div className="space-y-3">
                  <button
                    onClick={() => handleDataChange({ isNewcomer: false, arrivalMonth: '', originCity: '' })}
                    className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                      formData.isNewcomer === false
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="text-xl mr-3">🏠</span>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          No, I've lived here for a while
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          I'm used to the local climate
                        </div>
                      </div>
                      {formData.isNewcomer === false && (
                        <div className="text-blue-500 ml-2">✓</div>
                      )}
                    </div>
                  </button>
                  
                  <button
                    onClick={() => handleDataChange({ isNewcomer: true })}
                    className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                      formData.isNewcomer === true
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="text-xl mr-3">✈️</span>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          Yes, I moved here recently
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          I'm still adapting to the climate
                        </div>
                      </div>
                      {formData.isNewcomer === true && (
                        <div className="text-blue-500 ml-2">✓</div>
                      )}
                    </div>
                  </button>
                </div>
              </div>

              {formData.isNewcomer && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Where did you move from?
                    </label>
                    <AutocompleteInput
                      onLocationSelect={(locationData) => {
                        handleDataChange({ originCity: locationData.location });
                      }}
                      theme="light"
                      placeholder="Search for your previous city"
                      initialValue={formData.originCity}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      What month did you move here?
                    </label>
                    <select
                      value={formData.arrivalMonth}
                      onChange={(e) => handleDataChange({ arrivalMonth: e.target.value })}
                      className="w-full p-3 rounded-lg border border-gray-300 dark:border-gray-600 
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select month</option>
                      {Array.from({ length: 12 }, (_, i) => {
                        const date = new Date(2024, i, 1);
                        const value = date.toISOString().slice(0, 7);
                        const label = date.toLocaleDateString('en', { month: 'long', year: 'numeric' });
                        return (
                          <option key={value} value={value}>{label}</option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Default Activity
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'walk', label: 'Walking', icon: '🚶' },
                    { value: 'bike', label: 'Biking', icon: '🚴' },
                    { value: 'run', label: 'Running', icon: '🏃' },
                    { value: 'office', label: 'Office', icon: '💼' }
                  ].map((activity) => (
                    <button
                      key={activity.value}
                      onClick={() => handleDataChange({ defaultActivity: activity.value })}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        formData.defaultActivity === activity.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="text-xl mb-1">{activity.icon}</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {activity.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
            disabled={currentStep === 1}
            className="flex items-center space-x-1 px-3 py-2 text-gray-600 dark:text-gray-400 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Previous</span>
          </button>

          <div className="flex space-x-2">
            {hasChanges && (
              <button
                onClick={handleUndo}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-700 text-white rounded-lg"
              >
                Undo Changes
              </button>
            )}
            
            {currentStep < 3 && (
              <button
                onClick={() => setCurrentStep(prev => Math.min(3, prev + 1))}
                className="flex items-center space-x-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-lg"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;